/**
 * MosaicIntakeModal.js — v6.32.162
 *
 * Modal launched from each student row. Captures:
 *   - Demographic intake (grade, home language, years in U.S., SIFE,
 *     IEP/504, newcomer, former ELL, prior assessment, notes)
 *   - Optional starting MOSAIC levels per domain (1-5)
 *
 * On save: persists to /api/mosaic/intake/{student_id} and renders the
 * composed Action Panel inline so the teacher sees immediate value.
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import MosaicActionPanel from './MosaicActionPanel';
import MosaicBucketBadge from './MosaicBucketBadge';
import MosaicScreenerModal from './MosaicScreenerModal';
import MosaicOralCapture from './MosaicOralCapture';
import MosaicGrowthCard from './MosaicGrowthCard';
import MosaicRubricTrendCard from './MosaicRubricTrendCard';
import MosaicParentLinkModal from './MosaicParentLinkModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAINS = [
  { code: 'listening', label: 'Listening' },
  { code: 'speaking',  label: 'Speaking'  },
  { code: 'reading',   label: 'Reading'   },
  { code: 'writing',   label: 'Writing'   },
];

const LEVELS = [
  { v: 0, label: '— Not yet assessed' },
  { v: 1, label: '1 · Entering' },
  { v: 2, label: '2 · Emerging' },
  { v: 3, label: '3 · Developing' },
  { v: 4, label: '4 · Expanding' },
  { v: 5, label: '5 · Bridging' },
  { v: 6, label: '6 · Reaching' },
];

const BLANK = {
  grade: '',
  home_language: '',
  years_in_us: '',
  sife: false,
  iep_504: false,
  newcomer: false,
  former_ell: false,
  prior_assessment: '',
  listening: 0,
  speaking:  0,
  reading:   0,
  writing:   0,
  notes: '',
};

const MosaicIntakeModal = ({ open, onOpenChange, student }) => {
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [panel, setPanel] = useState(null);
  const [showScreener, setShowScreener] = useState(false);
  const [showParentLink, setShowParentLink] = useState(false);

  // Reset + fetch existing intake when opened.
  useEffect(() => {
    if (!open || !student?.user_id) return;
    setPanel(null);
    setForm(BLANK);
    setLoading(true);
    axios
      .get(`${API}/mosaic/intake/${student.user_id}`)
      .then((res) => {
        const it = res.data?.intake;
        if (it) {
          setForm({
            ...BLANK,
            ...it,
            years_in_us: it.years_in_us ?? '',
            sife: !!it.sife,
            iep_504: !!it.iep_504,
            newcomer: !!it.newcomer,
            former_ell: !!it.former_ell,
            listening: Number(it.listening) || 0,
            speaking:  Number(it.speaking)  || 0,
            reading:   Number(it.reading)   || 0,
            writing:   Number(it.writing)   || 0,
          });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, student?.user_id]);

  const hasAnyLevel = useMemo(
    () => DOMAINS.some((d) => Number(form[d.code]) > 0),
    [form]
  );

  const handleField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    if (!student?.user_id) return;
    setSaving(true);
    try {
      const body = { ...form };
      // Coerce empties.
      if (body.years_in_us === '') body.years_in_us = null;
      DOMAINS.forEach((d) => {
        body[d.code] = Number(body[d.code]) || 0;
      });
      const res = await axios.post(`${API}/mosaic/intake/${student.user_id}`, body);
      toast.success('MOSAIC intake saved');
      setPanel(res.data?.panel || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save intake');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="mosaic-intake-modal"
        className="max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            MOSAIC Intake
            {hasAnyLevel ? (
              <MosaicBucketBadge
                level={Math.max(1, Math.round(
                  (Number(form.listening) || 0) * 0.15 +
                  (Number(form.speaking)  || 0) * 0.15 +
                  (Number(form.reading)   || 0) * 0.35 +
                  (Number(form.writing)   || 0) * 0.35
                ))}
              />
            ) : null}
          </DialogTitle>
          <DialogDescription>
            {student?.name ? `Student: ${student.name}` : 'Student profile'} ·
            <span className="ml-1 text-gray-500">
              Independent MOSAIC framework — not derived from any third-party assessment provider.
            </span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-16 flex items-center justify-center text-gray-500" data-testid="mosaic-intake-loading">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <div className="space-y-6">
            {/* Demographic block */}
            <section>
              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
                Demographics
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="m-grade">Grade</Label>
                  <select
                    id="m-grade"
                    data-testid="m-grade"
                    className="w-full border border-gray-300 rounded h-10 px-2 text-sm"
                    value={form.grade || ''}
                    onChange={(e) => handleField('grade', e.target.value)}
                  >
                    <option value="">—</option>
                    {['K','1','2','3','4','5','6','7','8','9','10','11','12'].map((g) => (
                      <option key={g} value={g}>{g}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="m-home-language">Home Language</Label>
                  <Input
                    id="m-home-language"
                    data-testid="m-home-language"
                    value={form.home_language || ''}
                    onChange={(e) => handleField('home_language', e.target.value)}
                    placeholder="e.g. Spanish, Mandarin, Bengali"
                  />
                </div>
                <div>
                  <Label htmlFor="m-years-us">Years in U.S. Schools</Label>
                  <Input
                    id="m-years-us"
                    data-testid="m-years-us"
                    type="number"
                    min="0"
                    step="0.5"
                    value={form.years_in_us ?? ''}
                    onChange={(e) => handleField('years_in_us', e.target.value === '' ? '' : Number(e.target.value))}
                  />
                </div>
                <div>
                  <Label htmlFor="m-prior">Prior Proficiency Assessment</Label>
                  <Input
                    id="m-prior"
                    data-testid="m-prior"
                    value={form.prior_assessment || ''}
                    onChange={(e) => handleField('prior_assessment', e.target.value)}
                    placeholder="e.g. NYSITELL 2025 / Entering"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
                {[
                  { key: 'sife', label: 'SIFE' },
                  { key: 'iep_504', label: 'IEP / 504' },
                  { key: 'newcomer', label: 'Newcomer' },
                  { key: 'former_ell', label: 'Former ELL' },
                ].map((b) => (
                  <label
                    key={b.key}
                    className="flex items-center gap-2 text-sm border border-gray-300 px-3 py-2 cursor-pointer hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      data-testid={`m-flag-${b.key}`}
                      checked={!!form[b.key]}
                      onChange={(e) => handleField(b.key, e.target.checked)}
                    />
                    <span className="font-semibold">{b.label}</span>
                  </label>
                ))}
              </div>
            </section>

            {/* Starting levels */}
            <section>
              <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
                Starting MOSAIC Levels (optional)
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {DOMAINS.map((d) => (
                  <div key={d.code}>
                    <Label htmlFor={`m-level-${d.code}`}>{d.label}</Label>
                    <select
                      id={`m-level-${d.code}`}
                      data-testid={`m-level-${d.code}`}
                      className="w-full border border-gray-300 rounded h-10 px-2 text-sm"
                      value={form[d.code] || 0}
                      onChange={(e) => handleField(d.code, Number(e.target.value))}
                    >
                      {LEVELS.map((opt) => (
                        <option key={opt.v} value={opt.v}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              {/* v6.32.164 — Oral Language Capture auto-rates the Speaking domain */}
              <div className="mt-3">
                <MosaicOralCapture
                  studentId={student?.user_id}
                  onResult={(r) => {
                    if (r?.speaking_level) {
                      handleField('speaking', Number(r.speaking_level));
                    }
                  }}
                />
              </div>
            </section>

            {/* Notes */}
            <section>
              <Label htmlFor="m-notes">Teacher Notes</Label>
              <Textarea
                id="m-notes"
                data-testid="m-notes"
                rows={3}
                value={form.notes || ''}
                onChange={(e) => handleField('notes', e.target.value)}
                placeholder="Observations, family context, instructional needs…"
              />
            </section>

            {/* Action buttons */}
            <div className="flex items-center justify-between gap-2 pt-2 border-t border-gray-200">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setShowScreener(true)}
                  data-testid="m-launch-screener"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                >
                  Run 10-min Screener
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setShowParentLink(true)}
                  data-testid="m-send-parent-link"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  disabled={!student?.user_id}
                >
                  Send to Family
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  data-testid="m-cancel"
                >
                  Close
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="m-save"
                  className="bg-gray-900 hover:bg-gray-800 text-white"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Save MOSAIC Intake
                </Button>
              </div>
            </div>

            {/* Action panel (post-save) */}
            {panel ? (
              <section className="pt-4">
                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">
                  Teacher Action Panel
                </h4>
                <MosaicActionPanel panel={panel} />
              </section>
            ) : null}

            {/* v6.32.166 — Predicted Growth */}
            {student?.user_id ? (
              <section className="pt-2">
                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                  Predicted Growth
                </h4>
                <MosaicGrowthCard studentId={student.user_id} />
              </section>
            ) : null}

            {/* v6.32.167 — Rolling MOSAIC content engine Rubric Trend */}
            {student?.user_id ? (
              <section className="pt-2">
                <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">
                  Rolling Rubric Trend
                </h4>
                <MosaicRubricTrendCard studentId={student.user_id} />
              </section>
            ) : null}
          </div>
        )}
        <MosaicScreenerModal
          open={showScreener}
          onOpenChange={setShowScreener}
          student={student}
          grade={form.grade || '3'}
          onComplete={(res) => {
            // Reflect new levels into the form + show panel.
            if (res?.levels) {
              setForm((f) => ({ ...f, ...res.levels }));
              setPanel(res.panel || null);
            }
            setShowScreener(false);
          }}
        />
        <MosaicParentLinkModal
          open={showParentLink}
          onOpenChange={setShowParentLink}
          student={student}
        />
      </DialogContent>
    </Dialog>
  );
};

export default MosaicIntakeModal;
